import {
  EMAIL_TEMPLATE_CATEGORIES,
  EMAIL_TEMPLATE_CATEGORY_LABELS,
  type EmailTemplateCategory,
  type EmailTemplateDTO,
  type SearchResponse,
  type TemplateDraftPreviewBody,
  type TemplatePreviewDTO,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, FileText, Plus, Search } from 'lucide-react';
import { useRef, useState } from 'react';
import { Badge, Button, EmptyState, Field, Input, Select, SidePanel, Skeleton, Spinner, Switch, Textarea, useConfirm, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useLookups } from '../../hooks/useMeta';
import { api, apiUrl, errorMessage } from '../../lib/api';

/** Template library: seeded pizza-equipment templates plus the team's own. */
export function TemplatesTab() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EmailTemplateDTO | 'new' | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['templates', 'all'],
    queryFn: ({ signal }) => api.get<{ items: EmailTemplateDTO[] }>('/api/templates', { signal }),
  });
  const items = data?.items ?? [];

  // A copy opens straight in the editor: duplicating is how you start your own version of a template.
  const duplicate = useMutation({
    mutationFn: (template: EmailTemplateDTO) => api.post<EmailTemplateDTO>(`/api/templates/${template.id}/duplicate`),
    onSuccess: async (copy) => {
      toast.success(`Created “${copy.name}” — edit it to make it your own`);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      setEditing(copy);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[13px] text-slate-500">Variables like {'{{first_name}}'} and {'{{business_name}}'} are filled per recipient; add a fallback with {'{{first_name|there}}'}.</p>
        {can('templates:manage') && (
          <Button variant="primary" icon={Plus} onClick={() => setEditing('new')}>
            New template
          </Button>
        )}
      </div>
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <EmptyState icon={FileText} title="No templates" description="Create one for introductions, quote follow-ups and check-ins." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((template) => (
            <li key={template.id} className="flex flex-wrap items-center gap-2 py-2 text-[13px]">
              <button type="button" className="font-medium text-slate-900 hover:text-ember-700" onClick={() => setEditing(template)}>
                {template.name}
              </button>
              <Badge color="slate" size="sm">
                {EMAIL_TEMPLATE_CATEGORY_LABELS[template.category]}
              </Badge>
              {!template.isActive && <Badge color="gray" size="sm">inactive</Badge>}
              {template.usedBySequences > 0 && <Badge color="violet" size="sm">used by {template.usedBySequences} sequence(s)</Badge>}
              <span className="min-w-0 flex-1 truncate text-slate-500">{template.subject}</span>
              {can('templates:manage') && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Copy}
                  loading={duplicate.isPending && duplicate.variables?.id === template.id}
                  onClick={() => duplicate.mutate(template)}
                >
                  Duplicate
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <TemplateEditor
          key={editing === 'new' ? 'new' : editing.id}
          template={editing === 'new' ? null : editing}
          readOnly={!can('templates:manage')}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** The lead a template is previewed with; a contact hit previews for that contact. */
type PreviewLead = { businessId: string; contactId: string | null; label: string };

function TemplateEditor({ template, readOnly, onClose }: { template: EmailTemplateDTO | null; readOnly: boolean; onClose: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { meta } = useLookups();
  const [name, setName] = useState(template?.name ?? '');
  const [category, setCategory] = useState<EmailTemplateCategory>(template?.category ?? 'custom');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [previewLead, setPreviewLead] = useState<PreviewLead | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // Unsaved text previews too (new templates included), so problems show while typing rather than on save.
  const previewInput: TemplateDraftPreviewBody = {
    subject: useDebouncedValue(subject, 400),
    body: useDebouncedValue(body, 400),
    businessId: previewLead?.businessId ?? null,
    contactId: previewLead?.contactId ?? null,
  };
  const {
    data: preview,
    error: previewError,
    isFetching: previewing,
  } = useQuery({
    queryKey: ['templates', 'preview', previewInput],
    queryFn: ({ signal }) => api.post<TemplatePreviewDTO>('/api/templates/preview', previewInput, { signal }),
    enabled: previewInput.subject !== '' || previewInput.body !== '',
    placeholderData: (previous) => previous,
  });
  // The API validates exactly like a save does, so problems show while typing instead of after "Save".
  const problems = [
    ...(preview?.errors ?? []),
    ...(preview?.unknown ?? []).map((key) => `{{${key}}} is not a variable — it would be sent as literal text. Use one of the variables below.`),
  ];

  const save = useMutation({
    mutationFn: () =>
      template
        ? api.patch(`/api/templates/${template.id}`, { name, category, subject, body, isActive, version: template.version })
        : api.post('/api/templates', { name, category, subject, body, isActive }),
    onSuccess: async () => {
      toast.success('Template saved');
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = async () => {
    if (!template) return;
    if (!(await confirm({ title: `Delete "${template.name}"?`, message: 'Existing emails keep their content.', confirmLabel: 'Delete', danger: true }))) return;
    try {
      await api.delete(`/api/templates/${template.id}`);
      toast.success('Template deleted');
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  /** Puts the variable where the cursor is (or replaces the selection), then keeps typing after it. */
  const insert = (key: string) => {
    const token = `{{${key}}}`;
    const textarea = bodyRef.current;
    const start = textarea?.selectionStart ?? body.length;
    const end = textarea?.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title={template ? template.name : 'New template'}
      width="xl"
      footer={
        readOnly ? undefined : (
          <div className="flex items-center justify-between">
            {template ? (
              <Button variant="danger" onClick={() => void remove()}>
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="primary" loading={save.isPending} disabled={!name || !subject || !body} onClick={() => save.mutate()}>
                Save template
              </Button>
            </div>
          </div>
        )
      }
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <Field label="Name">
            <Input value={name} disabled={readOnly} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Category">
            <Select value={category} disabled={readOnly} onChange={(event) => setCategory(event.target.value as EmailTemplateCategory)}>
              {EMAIL_TEMPLATE_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {EMAIL_TEMPLATE_CATEGORY_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Subject">
            <Input value={subject} disabled={readOnly} onChange={(event) => setSubject(event.target.value)} />
          </Field>
          <Field label="Body">
            <Textarea ref={bodyRef} rows={14} value={body} disabled={readOnly} onChange={(event) => setBody(event.target.value)} />
          </Field>
          {problems.length > 0 && (
            <ul aria-live="polite" className="list-disc rounded-md border border-red-200 bg-red-50 p-2 pl-6 text-[13px] text-red-800">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
          {!readOnly && (
            <div>
              <p className="mb-1 text-xs text-slate-500">Insert a variable at the cursor:</p>
              <div className="flex flex-wrap gap-1">
                {(meta?.templateVariables ?? []).map((variable) => (
                  <button key={variable.key} type="button" title={variable.label} onClick={() => insert(variable.key)} className="rounded border border-slate-200 px-1.5 py-0.5 font-mono text-xs text-slate-600 hover:bg-slate-50">
                    {`{{${variable.key}}}`}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Switch checked={isActive} disabled={readOnly} onChange={setIsActive} label="Active (available in the composer)" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Live preview</p>
            {previewing && <Spinner className="size-3.5" />}
          </div>
          <PreviewLeadPicker lead={previewLead} leadName={preview?.lead?.businessName ?? null} onChange={setPreviewLead} />
          {previewError ? (
            <p className="rounded-md border border-red-200 bg-red-50 p-2 text-[13px] text-red-800">{errorMessage(previewError)}</p>
          ) : preview ? (
            <>
              <p className="text-xs text-slate-500">
                {preview.lead
                  ? preview.lead.email
                    ? `As ${preview.lead.contactName ?? 'the business address'} <${preview.lead.email}> would read it, with you as the sender.`
                    : 'This lead has no email address, so bulk email would skip it. Showing its details anyway.'
                  : 'With sample data (Marco Bellini at Pizzeria Fuoco Vivo).'}
              </p>
              <div className="rounded-md border border-slate-200 bg-surface p-3 text-[13px]">
                <p className="mb-2 font-medium text-slate-900">{preview.subject}</p>
                <pre className="whitespace-pre-wrap wrap-anywhere font-sans text-slate-700">{preview.text}</pre>
              </div>
              {preview.lead && preview.missing.length > 0 && (
                <p className="text-xs text-amber-700">Empty for this lead: {preview.missing.join(', ')} — the fallback is used if the template has one, otherwise it stays blank.</p>
              )}
            </>
          ) : subject === '' && body === '' ? (
            <p className="text-[13px] text-slate-500">Write a subject and body to see the email as a recipient would.</p>
          ) : (
            <Skeleton className="h-48 w-full" />
          )}
        </div>
      </div>
    </SidePanel>
  );
}

/** Choose what the preview is filled with: sample data, or a real lead found by name, contact, email or phone. */
function PreviewLeadPicker({ lead, leadName, onChange }: { lead: PreviewLead | null; leadName: string | null; onChange: (lead: PreviewLead | null) => void }) {
  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search.trim(), 250);
  const { data, isFetching } = useQuery({
    queryKey: ['search', q],
    queryFn: ({ signal }) => api.get<SearchResponse>(apiUrl('/api/search', { q, limit: 8 }), { signal }),
    enabled: q.length >= 2,
  });
  const hits = (data?.results ?? []).filter(
    (hit, index, all) => hit.type === 'contact' || (hit.type === 'business' && all.findIndex((other) => other.type === 'business' && other.businessId === hit.businessId) === index),
  );

  if (lead) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-[13px]">
        <span className="min-w-0 truncate">
          With <span className="font-medium text-slate-900">{leadName ?? lead.label}</span>
        </span>
        <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
          Use sample data
        </Button>
      </div>
    );
  }
  return (
    <div>
      <Input size="sm" icon={Search} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Preview with a real lead: name, email or phone" aria-label="Preview with a real lead" />
      {q.length >= 2 && (
        <ul className="mt-1 max-h-52 overflow-auto rounded-md border border-slate-200 bg-surface text-[13px]">
          {hits.length === 0 ? (
            <li className="px-2.5 py-1.5 text-slate-500">{isFetching ? 'Searching…' : 'No matching lead'}</li>
          ) : (
            hits.map((hit) => (
              <li key={`${hit.type}:${hit.id}`}>
                <button
                  type="button"
                  className="block w-full px-2.5 py-1.5 text-left hover:bg-slate-50"
                  onClick={() => {
                    onChange({ businessId: hit.businessId, contactId: hit.type === 'contact' ? hit.id : null, label: hit.title });
                    setSearch('');
                  }}
                >
                  <span className="font-medium text-slate-900">{hit.title}</span>
                  {hit.subtitle && <span className="ml-1.5 text-xs text-slate-500">{hit.subtitle}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
